import { Building2, MapPin, ExternalLink, Clock, CheckCircle2, FolderOpen, Lock, Mail, Briefcase, Calendar } from 'lucide-react';
import type { AuditorCompany } from '../../types/auditor';
import type { FiscalYear } from '../../types/fiscal-year';
import type { CreditScoreStats } from '../../services/api/endpoints/creditScore';
import { CreditScoreBadge } from './CreditScoreBadge';
import { getInitials } from '../../utils/creditScoreHelpers';

interface AuditorCompanyCardProps {
  company: AuditorCompany;
  isRecentlyOpened?: boolean;
  onOpen: (company: AuditorCompany) => void;
  creditScore?: CreditScoreStats | null;
  onCetClick?: (company: AuditorCompany) => void;
}

function FiscalYearPills({ summary }: { summary: AuditorCompany['fiscalYearsSummary'] }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {summary.open > 0 && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <FolderOpen className="h-3 w-3" />
          {summary.open} ouvert{summary.open > 1 ? 's' : ''}
        </span>
      )}
      {summary.closed > 0 && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <Lock className="h-3 w-3" />
          {summary.closed} clôturé{summary.closed > 1 ? 's' : ''}
        </span>
      )}
      {summary.audited > 0 && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <CheckCircle2 className="h-3 w-3" />
          {summary.audited} audité{summary.audited > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

const fyStatusColor: Record<string, string> = {
  open: 'bg-green-500',
  closed: 'bg-gray-400',
  audited: 'bg-blue-500',
};

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
}



export function AuditorCompanyCard({
  company,
  isRecentlyOpened = false,
  onOpen,
  creditScore,
  onCetClick,
}: AuditorCompanyCardProps) {
  const { organization: org, fiscalYearsSummary: summary, fiscalYears, membership } = company;
  const initials = getInitials(org.name);

  return (
    <div
      className="group relative bg-card border border-border rounded-xl p-4 sm:p-5 flex flex-col gap-2.5 sm:gap-3
        hover:shadow-md hover:border-primary/40 transition-all duration-200 cursor-pointer"
      onClick={() => onOpen(company)}
    >
      {/* Badge récemment ouvert — positionné sous le header pour éviter le chevauchement */}
      {isRecentlyOpened && (
        <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs bg-primary/10 text-primary font-medium z-10">
          <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
          Récent
        </span>
      )}

      {/* Logo + nom */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {org.logo ? (
            <img
              src={org.logo}
              alt={org.name}
              className="h-10 w-10 sm:h-14 sm:w-14 rounded-lg object-contain border border-border bg-muted"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('hidden');
              }}
            />
          ) : null}
          <div
            className={`h-10 w-10 sm:h-14 sm:w-14 rounded-lg flex items-center justify-center text-base sm:text-lg font-bold
              bg-primary/10 text-primary border border-primary/20 select-none
              ${org.logo ? 'hidden' : ''}`}
            aria-hidden
          >
            {initials || <Building2 className="h-5 w-5 sm:h-7 sm:w-7" />}
          </div>
        </div>

        <div className={`flex-1 min-w-0 pt-0.5 ${isRecentlyOpened ? 'pr-16 sm:pr-20' : ''}`}>
          <h3 className="font-semibold text-sm sm:text-base text-foreground truncate leading-tight group-hover:text-primary transition-colors">
            {org.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className="text-[10px] sm:text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {org.legalForm}
            </span>
            {org.currency && (
              <span className="text-[10px] sm:text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {org.currency}
              </span>
            )}
            {org.accountingMode && (
              <span className="hidden sm:inline-flex text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {org.accountingMode}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Identifiants légaux */}
      <div className="grid grid-cols-1 gap-1 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground font-medium shrink-0">RCCM</span>
          <span className="text-foreground font-mono truncate text-right">
            {org.registrationNumber || '—'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground font-medium shrink-0">NIF</span>
          <span className="text-foreground font-mono truncate text-right">
            {org.taxId || '—'}
          </span>
        </div>
      </div>

      {/* Coordonnées : localisation + email — masquées sur petits écrans */}
      <div className="hidden sm:block space-y-1 text-xs">
        {(org.address || org.city || org.country) && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {org.address || [org.city, org.country].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
        {org.email && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Mail className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{org.email}</span>
          </div>
        )}
        {org.industry && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Briefcase className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate capitalize">{org.industry.replace(/_/g, ' ')}</span>
          </div>
        )}
      </div>

      {/* Exercices fiscaux — résumé + liste individuelle (masquée mobile) */}
      <div className="space-y-1.5">
        <FiscalYearPills summary={summary} />
        {fiscalYears.length > 0 && (
          <div className="hidden sm:flex flex-col gap-1 overflow-hidden">
            {fiscalYears.slice(0, 3).map((fy: FiscalYear) => (
              <div key={fy.id} className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${fyStatusColor[fy.status] || 'bg-gray-400'}`} />
                <Calendar className="h-3 w-3 shrink-0" />
                <span className="font-medium text-foreground shrink-0">{fy.code}</span>
                <span className="text-muted-foreground truncate">
                  {formatShortDate(fy.startDate)} — {formatShortDate(fy.endDate)}
                </span>
              </div>
            ))}
            {fiscalYears.length > 3 && (
              <span className="text-xs text-muted-foreground pl-5">
                +{fiscalYears.length - 3} autre{fiscalYears.length - 3 > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Credit Score + CET */}
      <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
        <CreditScoreBadge
          creditScore={creditScore}
          company={company}
          onCetClick={onCetClick}
        />
      </div>

      {/* Membership info */}
      <div className="text-xs text-muted-foreground flex items-center justify-between border-t border-border pt-2">
        <span>
          Ajouté le {new Date(membership.addedAt).toLocaleDateString('fr-FR')}
        </span>
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
          membership.status === 'active'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {membership.status === 'active' ? 'Actif' : 'Inactif'}
        </span>
      </div>

      {/* Bouton Ouvrir */}
      <button
        className="mt-auto w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg
          bg-primary/10 text-primary text-sm font-medium
          hover:bg-primary hover:text-primary-foreground
          group-hover:bg-primary group-hover:text-primary-foreground
          transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={(e) => { e.stopPropagation(); onOpen(company); }}
      >
        <ExternalLink className="h-4 w-4" />
        Ouvrir la comptabilité
      </button>
    </div>
  );
}
